import { USER_DB_REF } from "../constants/firestore";
import { DEFAULT_CALCULATION_HISTORY_TABLE_FILTERS } from "../constants/flters";
import { DEFAULT_TABLE_PAGINATION } from "../constants/pagination";
import { Calculation, CalculationCaregiverData, CalculationFamilyData, CalculationWithCaregiverAndFamilyData } from "../types/calculation";
import { TablePaginationConfig, TablePaginationResponse } from "../types/common";
import { CalculationHistoryTableFilters } from "../types/filters";
import { FirestoreQueryConstraint } from "../types/firebase";
import { getUserCaregiver } from "./caregiovers";
import { firestoreBuildQuery, firestoreDocDelete, firestoreEndBefore, firestoreExecuteQuery, firestoreLimit, firestoreOrderBy, firestoreStartAfter, firestoreWhere } from "./common/firebase";
import { getUserFamily } from "./families";
import { getMaxPageNumber } from "./tablesPagination";

export async function initCalculationHistoryTable(
  uid: string
): Promise<TablePaginationResponse<Calculation>> {
  const { maxPage, size } = DEFAULT_TABLE_PAGINATION;
  const firstPageConfig: TablePaginationConfig = { page: 1, size, maxPage };
  const page = await loadCalculationHistoryNextTablePage(uid, firstPageConfig, DEFAULT_CALCULATION_HISTORY_TABLE_FILTERS);
  return page;
}

export async function loadCalculationHistoryNextTablePage(
  uid: string,
  paginationConfig: TablePaginationConfig,
  filters: CalculationHistoryTableFilters
): Promise<TablePaginationResponse<Calculation>> {
  const res: Calculation[] = [];
  const { size, lastDoc } = paginationConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  // filtri
  const { caregiver, family, from, result, to } = filters;
  if (caregiver) queryConstraints.push(firestoreWhere('caregiverId', '==', caregiver));
  if (family) queryConstraints.push(firestoreWhere('familyId', '==', family));
  if (from) queryConstraints.push(firestoreWhere('tsCreated', '>=', from));
  if (to) queryConstraints.push(firestoreWhere('tsCreated', '<=', to));
  if (result) queryConstraints.push(firestoreWhere('stringResult', '==', result));
  // paginazione
  queryConstraints.push(firestoreOrderBy('tsCreated', 'desc'));
  if (lastDoc) queryConstraints.push(firestoreStartAfter(lastDoc));
  queryConstraints.push(firestoreLimit(size));
  // esecuzione
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/calculationHistory`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Calculation));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function loadCalculationHistoryPreviousTablePage(
  uid: string,
  currentPageConfig: TablePaginationConfig,
  filters: CalculationHistoryTableFilters
): Promise<TablePaginationResponse<Calculation>> {
  const res: Calculation[] = [];
  const { size, firstDoc } = currentPageConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  // filtri
  const { caregiver, family, from, result, to } = filters;
  if (caregiver) queryConstraints.push(firestoreWhere('caregiverId', '==', caregiver));
  if (family) queryConstraints.push(firestoreWhere('familyId', '==', family));
  if (from) queryConstraints.push(firestoreWhere('tsCreated', '>=', from));
  if (to) queryConstraints.push(firestoreWhere('tsCreated', '<=', to));
  if (result) queryConstraints.push(firestoreWhere('stringResult', '==', result));
  // paginazione
  queryConstraints.push(firestoreOrderBy('tsCreated', 'desc'));
  queryConstraints.push(firestoreEndBefore(firstDoc));
  queryConstraints.push(firestoreLimit(size));
  // esecuzione
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/calculationHistory`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Calculation));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function getCalculationHistoryMaxPageNumber(
  uid: string,
  pageSize: number,
  filters?: CalculationHistoryTableFilters
): Promise<number> {
  const queryConstraints: FirestoreQueryConstraint[] = [];
  if (filters) {
    const { caregiver, family, from, result, to } = filters;
    if (caregiver) queryConstraints.push(firestoreWhere('caregiverId', '==', caregiver));
    if (family) queryConstraints.push(firestoreWhere('familyId', '==', family));
    if (from) queryConstraints.push(firestoreWhere('tsCreated', '>=', from));
    if (to) queryConstraints.push(firestoreWhere('tsCreated', '<=', to));
    if (result) queryConstraints.push(firestoreWhere('stringResult', '==', result));
  }
  return getMaxPageNumber(`${USER_DB_REF}/${uid}/calculationHistory`, pageSize, queryConstraints.length > 0 ? queryConstraints : undefined);
} 


export async function integrateCalculationWithFamilyAndCaregiverData(
  uid: string,
  calculation: Calculation[]
): Promise<CalculationWithCaregiverAndFamilyData[]> {
  const res: CalculationWithCaregiverAndFamilyData[] = [];
  for (const c of calculation) {
    const { familyId, caregiverId } = c;
    if (familyId && caregiverId) {
      const family = await getUserFamily(uid, familyId);
      const caregiver = await getUserCaregiver(uid, caregiverId);
      // se la badante o la famiglia con la quale è stato effettuato il calcolo vengono cancellati non si possono mostrare i loro dati
      const caregiverData: CalculationCaregiverData = { name: caregiver?.personalData?.name ?? '---', surname: caregiver?.personalData?.surname ?? '---' };
      const familyData: CalculationFamilyData = { name: family?.personalData?.name ?? '---', surname: family?.personalData?.surname ?? '---' };
      res.push({ ...c, caregiverData, familyData });
    }
  }
  return res;
}

export async function deleteCalculationRecord(
  uid: string,
  calculationId: string,
): Promise<void> {
  await firestoreDocDelete(`${USER_DB_REF}/${uid}/calculationHistory`, calculationId);
}